import { FormItemProps } from '../templates/FormProps';
import { MESSAGE } from '@vindr/services/constants/message.constant';
import { Form, Input } from 'antd';
import { Rule } from 'antd/es/form';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface FormInput extends Omit<FormItemProps, 'multiple'> {
  type?: 'text' | 'number';
  negativeNumber?: boolean;
  [key: string]: any;
}

const FormInput = ({
  name,
  label,
  placeholder,
  rules,
  disabled,
  required,
  type,
  form,
  className,
  negativeNumber = false,
  noStyle,
  trigger,
  ...props
}: FormInput): JSX.Element => {
  const { t } = useTranslation('Vindoc');
  const [value, setValue] = useState<string | number>(0);

  const rulesMemo = useMemo(() => {
    const rulesState: Rule[] = [];

    if (required) {
      rulesState.push({
        required: true,
        message: t(MESSAGE.INLINE.IEM_0001),
      });
    }

    if (type === 'number') {
      rulesState.push({
        validator: (rule, value) => {
          if (value && isNaN(value)) return Promise.reject(t('Please enter a number'));

          return Promise.resolve();
        },
        message: t('Please enter a number'),
      });

      if (!negativeNumber) {
        rulesState.push({
          validator: (rule, value) => {
            if (Number(value) < 0) return Promise.reject(t('Please enter a positive number'));

            return Promise.resolve(true);
          },
          message: t('Please enter a positive number'),
        });
      }
    }

    if (rules) rulesState.push(...rules);

    return rulesState;
  }, [t, required, rules, type, negativeNumber]);

  return (
    <Form.Item
      name={name}
      {...(label && { label: t(label) })}
      rules={rulesMemo}
      className={className}
      noStyle={noStyle}
      {...(type === 'number' &&
        negativeNumber === false &&
        +value < 0 && {
          validateStatus: 'error',
          help: t('Please enter a positive number'),
        })}
      {...(trigger && { trigger })}
    >
      <Input
        placeholder={t(placeholder)}
        disabled={disabled}
        required={required}
        onChange={(e) => {
          if (type === 'number') {
            setValue(e.target.value);
            const num = +e.target.value;

            if ([0, ''].includes(num)) {
              form.setFieldsValue({ [name]: '' });

              return;
            }

            if (!isNaN(num)) {
              form.setFieldsValue({ [name]: num });
            }
          }
        }}
        {...props}
      />
    </Form.Item>
  );
};

export default FormInput;
